import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { OverlayPanel } from "primereact/overlaypanel";

import { Paths } from "@/core/constants/paths";
import { getPersistedUser, logoutFromBff } from "@/modules/auth";
import AcessibilityIcon from "@/assets/icons/acessibility.svg?react";
import styles from "./user-menu.module.scss";

const EDIT_HINT =
  "Para editar seus dados de acesso ao Hub, atualize sua conta no site da Microsoft.";

function getInitials(name?: string): string | null {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) return null;

  const first = parts[0]![0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]![0]! : "";
  return `${first}${last}`.toUpperCase();
}

interface UserMenuProps {
  collapsed: boolean;
  triggerTestId?: string;
  openBelow?: boolean;
}

export function UserMenu({
  collapsed,
  triggerTestId = "sidebar-user-button",
  openBelow = false,
}: UserMenuProps) {
  const navigate = useNavigate();
  const panelRef = useRef<OverlayPanel>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const user = getPersistedUser();
  const displayName = user?.name?.trim() || "Usuário";
  const area = user?.area?.trim();
  const jobTitle = user?.jobTitle?.trim();
  const initials = getInitials(user?.name);

  const avatarProps = initials ? { label: initials } : { icon: "pi pi-user" };

  async function handleLogout() {
    setIsLoggingOut(true);

    try {
      await logoutFromBff();
    } finally {
      navigate(Paths.LOGIN, {
        replace: true,
        state: {
          logoutMessage: {
            code: "MS001",
            severity: "success",
            summary: "Sucesso",
            detail: "Sua sessão foi encerrada com sucesso.",
          },
        },
      });
    }
  }

  return (
    <>
      <button
        type="button"
        className={`${styles.trigger} ${collapsed ? styles.triggerCollapsed : ""}`}
        onClick={(event) => panelRef.current?.toggle(event)}
        aria-label={`Perfil de ${displayName}`}
        aria-haspopup="dialog"
        aria-expanded={isPanelOpen}
        data-testid={triggerTestId}
      >
        <Avatar className={styles.triggerAvatar} shape="circle" {...avatarProps} />

        {!collapsed ? (
          <>
            <span className={styles.triggerInfo}>
              <span className={styles.triggerNameRow}>
                <span className={styles.triggerName}>{displayName}</span>
                <i className={`pi pi-chevron-up ${styles.triggerChevron}`} aria-hidden />
              </span>
              {area ? <span className={styles.triggerArea}>{area}</span> : null}
            </span>
          </>
        ) : null}
      </button>

      <OverlayPanel
        ref={panelRef}
        className={`${styles.panel} ${openBelow ? styles.panelBelow : ""}`}
        dismissable
        showCloseIcon={false}
        onShow={() => setIsPanelOpen(true)}
        onHide={() => setIsPanelOpen(false)}
      >
        <div
          className={styles.panelBody}
          data-testid="user-panel"
          role="dialog"
          aria-label="Informações do usuário"
        >
          <div className={styles.panelHeader}>
            <Avatar className={styles.panelAvatar} shape="circle" {...avatarProps} />
            <span className={styles.panelIdentity}>
              <span className={styles.panelName}>{displayName}</span>
              {area ? <span className={styles.panelArea}>{area}</span> : null}
            </span>
          </div>

          <dl className={styles.infoList}>
            {user?.email ? (
              <div className={styles.infoRow}>
                <dt className={styles.infoTerm}>
                  <i className={`pi pi-envelope ${styles.infoIcon}`} aria-hidden />
                  <span className={styles.srOnly}>E-mail</span>
                </dt>
                <dd className={styles.infoValue}>{user.email}</dd>
              </div>
            ) : null}

            {jobTitle ? (
              <div className={styles.infoRow}>
                <dt className={styles.infoTerm}>
                  <i className={`pi pi-briefcase ${styles.infoIcon}`} aria-hidden />
                  <span className={styles.srOnly}>Cargo</span>
                </dt>
                <dd className={styles.infoValue}>{jobTitle}</dd>
              </div>
            ) : null}
          </dl>

          <Divider className={styles.panelDivider} />

          <p className={styles.hint}>
            <i className={`pi pi-info-circle ${styles.hintIcon}`} aria-hidden />
            <span>{EDIT_HINT}</span>
          </p>

          <Divider className={styles.panelDivider} />

          <div className={styles.panelActions}>
            <Button
              type="button"
              className={styles.accessibilityButton}
              label="Acessibilidade"
              icon={
                <AcessibilityIcon className="p-button-icon p-button-icon-left" aria-hidden="true" />
              }
              outlined
              data-testid="sidebar-accessibility-button"
            />

            <Button
              type="button"
              className={styles.logoutButton}
              label="Sair"
              icon="pi pi-sign-out"
              onClick={handleLogout}
              loading={isLoggingOut}
              outlined
              data-testid="sidebar-logout-button"
            />
          </div>
        </div>
      </OverlayPanel>
    </>
  );
}
